import { DateTime } from 'luxon';

export let TZ = 'Europe/London';
export function setTimezone(tz: string) { TZ = tz; }

export const dt = (iso: string | Date | null | undefined) => (iso ? (iso instanceof Date ? DateTime.fromJSDate(iso) : DateTime.fromISO(iso)).setZone(TZ) : null);
export const fmtDateTime = (iso: string | Date | null | undefined) => dt(iso)?.toFormat('ccc d LLL HH:mm') ?? '–';
export const fmtDate = (iso: string | null | undefined) => (iso ? DateTime.fromISO(iso, { zone: TZ }).toFormat('ccc d LLL yyyy') : '–');
export const fmtShortDate = (iso: string | null | undefined) => (iso ? DateTime.fromISO(iso, { zone: TZ }).toFormat('d LLL') : '–');
export const fmtTime = (iso: string | null | undefined) => dt(iso)?.toFormat('HH:mm') ?? '';
/** Labour on a job, operation or booking (estimate, booked, actual): 2 decimal places, so quarter hours show exactly. */
export const fmtHours = (h: number | null | undefined) => (h === null || h === undefined ? '–' : `${Math.round(h * 100) / 100}h`);
/** Capacity and load for a person, day, week or site (available, scheduled, free, over, totals): 1 decimal place. */
export const h1 = (h: number | null | undefined) => (h === null || h === undefined ? '–' : `${Math.round(h * 10) / 10}h`);
/** "1 booking", "3 bookings". */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const d = dt(iso)!;
  return d.toRelative() ?? d.toFormat('d LLL HH:mm');
};
export const toLocalInput = (iso: string | null | undefined) => (iso ? dt(iso)!.toFormat("yyyy-LL-dd'T'HH:mm") : '');
export const fromLocalInput = (v: string) => (v ? DateTime.fromISO(v, { zone: TZ }).toISO()! : null);

export const STATUS_LABEL: Record<string, string> = {
  unscheduled: 'Unscheduled', planned: 'Planned', ready: 'Ready', in_progress: 'In progress', paused: 'Paused', waiting: 'Waiting',
  complete: 'Complete', cancelled: 'Cancelled', source_deleted: 'Source deleted',
};
export const RISK_LABEL: Record<string, string> = { on_schedule: 'On schedule', at_risk: 'At risk', predicted_late: 'Predicted late', overdue: 'Overdue', not_assessed: 'Not assessed' };
export const PRIORITY_LABEL: Record<string, string> = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };
export const EXCEPTION_LABEL: Record<string, string> = {
  no_routing: 'No routing', no_sub_group: 'No product sub group', no_skill_for_sub_group: 'No skill for the sub group', no_bom_time: 'No BOM production time', no_qualified_resource: 'No one qualified', source_changed: 'Assembly changed',
  source_completed: 'Assembly completed',
};
/** What the planner should do next for each exception. */
export const EXCEPTION_ACTION: Record<string, string> = {
  no_routing: 'Apply a routing', no_sub_group: 'Set product sub group', no_skill_for_sub_group: 'Link skill on Skill matrix',
  no_bom_time: 'Add BOM time', no_qualified_resource: 'Train or override', source_changed: 'Review change',
  source_completed: 'Acknowledge',
};
/** Exception label colour: resource and capacity lavender, parts amber, everything else (data and source) sky blue. */
export const exceptionSeverity = (code: string | null | undefined) => (code === 'no_qualified_resource' || code === 'no_skill_for_sub_group' || code === 'no_capacity' ? 'lav' : code === 'parts' || code === 'material' ? 'amber' : 'sky');
export const CATEGORY_LABEL: Record<string, string> = {
  holiday: 'Holiday', sickness: 'Sickness', training: 'Training', unpaid: 'Unpaid absence', appointment: 'Appointment', other_unavailable: 'Unavailable', absent: 'Absent',
  overtime: 'Overtime', extra_availability: 'Extra availability',
};
export const STRATEGY_LABEL: Record<string, string> = { earliest: 'Earliest available', balance: 'Balance workload', due_date: 'Meet due date' };
export const riskClass = (r: string) => (r === 'on_schedule' ? 'green' : r === 'at_risk' ? 'risk' : r === 'not_assessed' ? '' : 'red');
export const statusClass = (s: string) => ({ unscheduled: 'amber', planned: 'blue', ready: 'green', in_progress: 'sky', paused: 'sky', waiting: 'lav', complete: 'strong' } as Record<string, string>)[s] ?? '';
export const priorityClass = (p: string) => (p === 'urgent' ? 'red' : p === 'high' ? 'amber' : '');

/** A valid #rrggbb colour, or null. */
export function hexColour(colour: string | null | undefined): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((colour ?? '').trim());
  if (!m) return null;
  return `#${(m[1].length === 3 ? m[1].split('').map((x) => x + x).join('') : m[1]).toUpperCase()}`;
}

/** Dark or white text, whichever reads better on the colour. */
export function readableText(bg: string): string {
  const n = parseInt(bg.slice(1), 16);
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(n >> 16) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return L > 0.36 ? '#161413' : '#FFFFFF';
}

/** Marker colour for a status (dots, list markers): the status's own colour, exactly as set in Settings. */
export function mute(colour: string | null | undefined): string {
  return hexColour(colour) ?? '#E6E6E6';
}

/**
 * Scheduled-work fill by operation type (fixed values). Deterministic: the same operation type always gets the same
 * colour. The operation name is checked first, then the skill name; anything else is "other scheduled operation".
 */
export const OPERATION_TONES = {
  inspection: { label: 'Inspection', bg: '#5BB8F0', text: '#0B3A5C' },
  qa: { label: 'QA / quality', bg: '#F07FB0', text: '#4A0F28' },
  dispatch: { label: 'Package & dispatch', bg: '#A98BF0', text: '#24135E' },
  sink: { label: 'Sink assembly', bg: '#F2B33D', text: '#4A3000' },
  panel: { label: 'Panel build', bg: '#2BB5AC', text: '#062F2C' },
  electrical: { label: 'Cables / electrical', bg: '#C2DC3C', text: '#353F05' },
  mechanical: { label: 'Mechanical assembly', bg: '#B98A5E', text: '#2E1A08' },
  other: { label: 'Other operation', bg: '#BDBDB6', text: '#333333' },
} as const;
export type OperationTone = (typeof OPERATION_TONES)[keyof typeof OPERATION_TONES];

const OP_RULES: [RegExp, keyof typeof OPERATION_TONES][] = [
  [/inspect/i, 'inspection'],
  [/\bqa\b|quality/i, 'qa'],
  [/packag|dispatch|\bpack\b/i, 'dispatch'],
  [/sink/i, 'sink'],
  [/panel build|^panels?\b|build.*panel/i, 'panel'],
  [/electric|control panel|wiring|cabl|harness|loom|\blead\b/i, 'electrical'],
  [/mechanic/i, 'mechanical'],
];
const PRODUCT_TYPE_TONE: Record<string, keyof typeof OPERATION_TONES> = { Cables: 'electrical', Panels: 'panel', Sinks: 'sink', Mechanical: 'mechanical' };
/** Operation name first, then skill name, then the job's product type (sub group or product type rules, from the server). */
export function operationTone(opName?: string | null, skillName?: string | null, productType?: string | null): OperationTone {
  for (const text of [opName, skillName]) {
    if (!text) continue;
    for (const [re, key] of OP_RULES) if (re.test(text)) return OPERATION_TONES[key];
  }
  const k = productType ? PRODUCT_TYPE_TONE[productType] : undefined;
  return k ? OPERATION_TONES[k] : OPERATION_TONES.other;
}

/** Application palette (fixed values). */
export const PALETTE = {
  blue: '#3F6FD8', paleBlue: '#C9D8FA', sky: '#A9D6F5', strongGreen: '#39A872', paleGreen: '#BBE2C9', midGreen: '#92CEAC',
  mint: '#94ECCC', amber: '#EEC884', pink: '#ED97BA', lavender: '#D8D6FA', paleRed: '#F3C7C2', red: '#D94A40', grey: '#E6E6E6',
} as const;

type Tone = { bg: string; text: string };
const T = {
  kitted: { bg: '#C9D8FA', text: '#304E93' }, info: { bg: '#94ECCC', text: '#285C4B' }, progress: { bg: '#e9e4dd', text: '#4a4440' },
  notStarted: { bg: '#ED97BA', text: '#5F2940' }, rework: { bg: '#D8D6FA', text: '#49458C' }, quality: { bg: '#EEC884', text: '#5C4315' },
  green: { bg: '#BBE2C9', text: '#28563C' }, mint: { bg: '#94ECCC', text: '#285C4B' }, overdue: { bg: '#F3C7C2', text: '#A63B32' },
  sky: { bg: '#A9D6F5', text: '#28506B' },
} satisfies Record<string, Tone>;

/** Job status colours: the status's own colour from Settings, exactly, with readable text; by name when it has none. */
export function statusTone(name: string | null | undefined, colour?: string | null): Tone {
  const hex = hexColour(colour);
  if (hex) return { bg: hex, text: readableText(hex) };
  const n = (name ?? '').toLowerCase();
  if (/parts? ordered/.test(n)) return T.notStarted;
  if (/parts? missing|waiting for parts/.test(n)) return T.quality;
  if (/part kitted/.test(n)) return T.mint;
  if (/part complete|can be kitted|ready to kit|^complete|^done/.test(n)) return T.green;
  if (/kitted/.test(n)) return T.kitted;
  if (/in progress/.test(n)) return T.progress;
  if (/rework/.test(n)) return T.rework;
  if (/quality|\bqa\b/.test(n)) return T.quality;
  return T.progress;
}

/** Skill level colour: not trained grey, then pale blue, sky, mint and strong green at the top level. */
export function levelTone(level: number, max: number): Tone {
  if (!level || level <= 0) return { bg: '#e9e4dd', text: '#4a4440' };
  const scale: Tone[] = [{ bg: '#C9D8FA', text: '#304E93' }, { bg: '#A9D6F5', text: '#28506B' }, { bg: '#94ECCC', text: '#285C4B' }, { bg: '#39A872', text: '#FFFFFF' }];
  const i = Math.min(3, Math.max(0, Math.round((level / Math.max(1, max)) * 4) - 1));
  return scale[i];
}

/** A skill's identity colour: the same colour its operations have on the planning board. */
export const skillTone = (skillName: string | null | undefined) => operationTone(null, skillName);

/** Parts check: why a purchase order date is a guess. */
export const CHASE: Record<string, string> = { po_date_missing: 'PO has no delivery date', po_date_passed: 'PO delivery date passed' };
/** "need 4, have 2, due 16 Oct (PO-1)" */
export function shortText(x: any) {
  const due = x.due ? `, due ${fmtDate(x.due)}${x.orders?.length ? ` (${x.orders.join(', ')})` : ''}${x.dateFlag ? ` · ${CHASE[x.dateFlag]}` : ''}` : x.onPurchase ? `, ${fmtQty(x.onPurchase)} on order` : '';
  return `need ${fmtQty(x.required)}, have ${fmtQty(x.available)}${due}`;
}
const fmtQty = (n: number) => String(Math.round(Number(n) * 1000) / 1000);

/** A job is shown by its part number; by its reference (eg ASM-00011260) where it has none. */
export const partOf = (j?: { productCode?: string | null; reference?: string | null } | null) => j?.productCode || j?.reference || '';
